/**
 * SheetGrid - the recruitment list as a spreadsheet (docs/plans/crm-recruitment-list-plan.md Part
 * A sections 3 and 4, decisions 6, 9 and 10, B4).
 *
 * A real <table role="grid">: the page scrolls it (useWindowedSlice with scroller 'window', spacer
 * rows in its own <tbody>), the tick box and the name stay pinned on the left while the rest
 * scrolls sideways inside an overflow-x box, and the header row stays under the CRM's h-12 app
 * bar. That box's own scrollbar would sit under the last row, so it is hidden and a mirror bar
 * sticky at the window's bottom edge scrolls it instead. Sticky `top` cannot do that last part from inside a sideways scroller (the box would become
 * its scroll container, and the header would be pushed down over the first rows), so the header
 * cells are moved down with a transform driven by the window's scroll, the one number
 * `--sheet-head-y` on the table.
 *
 * Rows open like real links: the name is a RowLink, a click on a plain cell opens the lead (Ctrl,
 * Cmd and the middle button open a new tab), and every cell that hosts a control keeps its clicks.
 * A row closed on this study (a closed status) is greyed and stays where it is until the page
 * refreshes; its controls stay full strength because they still work (decision 15).
 *
 * Keyboard (recruitLogic's machine, sheetKeyAction / moveCursor / enterCell / escape): arrows move
 * the cursor cell, Enter opens the status, recruiter, note or attempt editor under it, Tab walks
 * across, Esc closes an editor, "o" opens the lead in a new tab, "/" jumps to the search box. The
 * grid keeps focus itself (aria-activedescendant names the cursor cell); an open editor owns every
 * key but Esc and Tab, and a dialog raised from a cell owns all of them, even while focus sits
 * behind it. When an edit's control goes away under the keyboard (a cell's dialog cancelled, a
 * save disabling the control) the grid ends the edit and takes the keyboard back, and focus that
 * leaves the grid for anywhere else ends the edit too.
 */
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import CallButton from '../../components/CallButton';
import { RowLink, eligibilityBadge, useRowNav } from '../../components/shared';
import { Z } from '../../components/ui';
import { dateTime, formatPhone, prettySlug, relTime } from '../../lib';
import { contactName } from '../../types';
import { useWindowedSlice } from '../../useWindowedSlice';
import {
  NO_STUDY_FORM_TITLE,
  STUDY_ELIGIBILITY_TITLE,
  answerKeyOf,
  enterCell,
  escape as escapeCursor,
  isAnswerColumnKey,
  isTerminalStatus,
  moveCursor,
  sheetKeyAction,
  type CursorState,
  type EditingCell,
} from '../recruitLogic';
import type {
  RecruitRow,
  RecruitRowChangeHandler,
  RecruitSelectHandler,
  RecruitSort,
  RecruitStudy,
  SheetColumn,
  SheetColumnKey,
} from '../types';
import AnswerCell from './cells/AnswerCell';
import AttemptsCell from './cells/AttemptsCell';
import CallbackCell from './cells/CallbackCell';
import FlagsCell from './cells/FlagsCell';
import LastTouchCell from './cells/LastTouchCell';
import NoteCell from './cells/NoteCell';
import OwnerCell from './cells/OwnerCell';
import ReasonCell from './cells/ReasonCell';
import SelectCell, { CHECKBOX_CLS } from './cells/SelectCell';
import StatusCell from './cells/StatusCell';

export interface SheetGridProps {
  /** The rows in the list's order, after its filters and sort. */
  rows: readonly RecruitRow[];
  study: RecruitStudy;
  /** The visible columns, in order (recruitLogic visibleColumns). */
  columns: readonly SheetColumn[];
  /** The header sort; null = needs-first. */
  sort: RecruitSort | null;
  onSortChange: (next: RecruitSort | null) => void;
  /** The ticked rows, by contact id. */
  selection: ReadonlySet<string>;
  onSelect: RecruitSelectHandler;
  /** The header box: tick or untick every row on the list. */
  onSelectAll: (selected: boolean) => void;
  onRowChange: RecruitRowChangeHandler;
  /** The cell whose editor is open (null = none), so a refresh leaves its draft alone. */
  onEditingChange?: (cell: EditingCell | null) => void;
  /** "/" on the sheet: focus the page's search box. */
  onFocusSearch?: () => void;
  /** The grid's accessible name. */
  label: string;
}

/** Where the CRM's sticky app bar ends (h-12): the header row stays just below it. */
const APP_BAR_PX = 48;

/** Pinned on the left while the rest scrolls sideways (recruitLogic pins them first). */
const PINNED: ReadonlySet<string> = new Set(['select', 'name']);

/** Columns whose cell hosts its own control: a click there never opens the lead. */
const CONTROL_COLUMNS: ReadonlySet<string> = new Set([
  'select', 'phone', 'status', 'owner', 'note', 'attempts', 'callback', 'flags',
]);

/**
 * The narrowest these columns render: their controls need the room (recruitLogic's width
 * otherwise). Phone fits a full US number beside the Call button; status fits the longest
 * pill, "Qualified - Pending Records", with its chevron.
 */
const MIN_WIDTH: Readonly<Record<string, number>> = {
  phone: 188,
  status: 224,
  attempts: 196,
  note: 240,
};

/** The width a column renders at. */
export function renderedWidth(column: Pick<SheetColumn, 'key' | 'width'>): number {
  const floor = Object.prototype.hasOwnProperty.call(MIN_WIDTH, column.key) ? MIN_WIDTH[column.key] : 0;
  return Math.max(column.width, floor);
}

/** Where a lead came in on this study, in words: the lead's own source, else how they joined. */
const JOINED_BY: Readonly<Record<string, string>> = {
  lead: 'Form',
  form: 'Form',
  import: 'Import',
  recruiter: 'Added by hand',
  pivot: 'Moved from another study',
  ctms: 'CTMS',
  agent: 'Texting agent',
  backfill: 'Backfill',
  trigger: 'Automatic',
};

function cameInSource(row: RecruitRow, study: RecruitStudy): string {
  const link = (row.contact.studies || []).find((s) => s.study_slug === study.slug && s.lead_source);
  if (link?.lead_source) return prettySlug(link.lead_source);
  const source = row.membership.source;
  if (source && Object.prototype.hasOwnProperty.call(JOINED_BY, source)) return JOINED_BY[source];
  return source ? prettySlug(source) : '';
}

const CALL_BTN_CLS =
  'inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 px-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:h-7 sm:min-h-0';

const clamp = (n: number, count: number): number => (count <= 0 ? 0 : Math.min(Math.max(0, n), count - 1));

/** A frame from now (a timer where there are no frames). */
function nextFrame(fn: () => void): number {
  return typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame(fn)
    : window.setTimeout(fn, 16);
}

function cancelFrame(id: number): void {
  if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(id);
  else window.clearTimeout(id);
}

/** A control that owns the keys while it has focus (typing, a native select's arrows, a menu). */
function isEditorTarget(el: HTMLElement): boolean {
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return type !== 'checkbox' && type !== 'radio' && type !== 'button' && type !== 'submit';
  }
  return !!el.closest('[role="menu"]');
}

/** Nothing has the keyboard: focus is on <body>, or on an element that has left the page. */
function focusIsNowhere(): boolean {
  const el = document.activeElement;
  return !el || el === document.body || !el.isConnected;
}

/** A form control that is disabled (a select or button mid-save). */
const isDisabledControl = (el: Element): boolean => (el as HTMLButtonElement).disabled === true;

const stop = (e: ReactMouseEvent) => e.stopPropagation();

type RowNav = ReturnType<typeof useRowNav>;

interface SheetRowProps {
  row: RecruitRow;
  /** The row's place in `rows`. */
  index: number;
  columns: readonly SheetColumn[];
  study: RecruitStudy;
  gridId: string;
  /** Left offsets of the pinned columns. */
  leftOf: ReadonlyMap<string, number>;
  selected: boolean;
  /** The cursor's column on this row, or -1 when the cursor is on another row. */
  cursorCol: number;
  /** The cursor cell's editor is open. */
  editing: boolean;
  /** The grid has focus: draw the cursor. */
  active: boolean;
  rowNav: RowNav;
  onSelect: RecruitSelectHandler;
  onRowChange: RecruitRowChangeHandler;
  onCellMouseDown: (row: number, col: number) => void;
  onEditEnd: () => void;
  onNoteEditing: (contactId: string, open: boolean) => void;
}

const SheetRow = memo(function SheetRow({
  row,
  index,
  columns,
  study,
  gridId,
  leftOf,
  selected,
  cursorCol,
  editing,
  active,
  rowNav,
  onSelect,
  onRowChange,
  onCellMouseDown,
  onEditEnd,
  onNoteEditing,
}: SheetRowProps) {
  const c = row.contact;
  const id = c.id;
  const href = `/crm/contacts/${id}`;
  const name = contactName(c);
  const closed = isTerminalStatus(row.membership.status);
  const replyOwed = row.reason?.kind === 'needs_reply';
  const onNoteOpen = useCallback((open: boolean) => onNoteEditing(id, open), [onNoteEditing, id]);

  const cell = (col: SheetColumn, isCursor: boolean) => {
    const edit = isCursor && editing;
    switch (col.key) {
      case 'select':
        return <SelectCell row={row} selected={selected} onSelect={onSelect} />;
      case 'name':
        return (
          <div className="flex min-w-0 items-center gap-1.5">
            <RowLink
              to={href}
              title={name}
              className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${replyOwed ? 'font-semibold' : 'font-medium'}`}
            >
              {name}
            </RowLink>
            {replyOwed && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Replied, waiting on us" />}
          </div>
        );
      case 'phone':
        return (
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate whitespace-nowrap text-xs tabular-nums text-gray-700 dark:text-gray-200">
              {c.phone_e164 ? formatPhone(c.phone_e164) : '—'}
            </span>
            {c.phone_e164 && <CallButton to={c.phone_e164} contactId={id} name={name} className={CALL_BTN_CLS} />}
          </div>
        );
      case 'status':
        return <StatusCell row={row} study={study} onRowChange={onRowChange} editing={edit} onEditEnd={onEditEnd} />;
      case 'reason':
        return <ReasonCell row={row} study={study} />;
      case 'owner':
        return <OwnerCell row={row} onRowChange={onRowChange} editing={edit} onEditEnd={onEditEnd} />;
      case 'note':
        return (
          <NoteCell
            row={row}
            study={study}
            onRowChange={onRowChange}
            editing={edit}
            onEditEnd={onEditEnd}
            onEditingChange={onNoteOpen}
          />
        );
      case 'attempts':
        return <AttemptsCell row={row} study={study} onRowChange={onRowChange} editing={edit} onEditEnd={onEditEnd} />;
      case 'last_touch':
        return <LastTouchCell row={row} />;
      case 'callback':
        return <CallbackCell row={row} study={study} onRowChange={onRowChange} />;
      case 'came_in': {
        const source = cameInSource(row, study);
        return (
          <div className="min-w-0 text-xs" title={dateTime(row.membership.first_considered_at)}>
            <div className="truncate text-gray-700 dark:text-gray-200">{relTime(row.membership.first_considered_at)}</div>
            {source && <div className="truncate text-gray-400 dark:text-gray-500">{source}</div>}
          </div>
        );
      }
      case 'eligibility': {
        // This study's own form only: another study's verdict never shows here.
        const badge = eligibilityBadge(c.study_eligibility ?? null);
        return badge
          ? <span title={STUDY_ELIGIBILITY_TITLE} className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>{badge.label}</span>
          : <span title={NO_STUDY_FORM_TITLE} className="text-sm text-gray-400 dark:text-gray-500">—</span>;
      }
      case 'flags':
        return <FlagsCell row={row} study={study} onRowChange={onRowChange} />;
      default:
        return isAnswerColumnKey(col.key)
          ? <AnswerCell row={row} questionKey={answerKeyOf(col.key)} label={col.label} />
          : null;
    }
  };

  const bg = selected
    ? 'bg-primary-50 dark:bg-gray-700'
    : 'bg-white group-hover:bg-gray-50 dark:bg-gray-800 dark:group-hover:bg-gray-700';

  return (
    <tr
      data-row={index}
      data-contact={id}
      data-closed={closed ? 'true' : undefined}
      aria-rowindex={index + 2}
      aria-selected={selected}
      {...rowNav(href)}
      className="group cursor-pointer"
    >
      {columns.map((col, ci) => {
        const isCursor = ci === cursorCol;
        const pinned = PINNED.has(col.key);
        const control = CONTROL_COLUMNS.has(col.key);
        const style: CSSProperties | undefined = pinned ? { left: leftOf.get(col.key) ?? 0 } : undefined;
        return (
          <td
            key={col.key}
            id={`${gridId}-c-${index}-${ci}`}
            data-cell={`${index}:${ci}`}
            data-col={col.key}
            data-cursor={isCursor ? 'true' : undefined}
            data-editing={isCursor && editing ? 'true' : undefined}
            style={style}
            onMouseDown={() => onCellMouseDown(index, ci)}
            onClick={control ? stop : undefined}
            onAuxClick={control ? stop : undefined}
            className={`border-b border-gray-100 px-2 py-1.5 align-middle transition-colors duration-150 dark:border-gray-700/70 ${
              pinned ? `sticky ${Z.rail}` : ''
            } ${bg} ${isCursor && active ? 'ring-2 ring-inset ring-primary-500' : ''}`}
          >
            {/* Greyed content, never a greyed cell: the pinned cells must stay opaque, and a
                control cell (which can open a modal) must not become a stacking context. */}
            <div className={closed && !control ? 'min-w-0 opacity-60' : 'min-w-0'}>{cell(col, isCursor)}</div>
          </td>
        );
      })}
    </tr>
  );
});

export function SheetGrid({
  rows,
  study,
  columns,
  sort,
  onSortChange,
  selection,
  onSelect,
  onSelectAll,
  onRowChange,
  onEditingChange,
  onFocusSearch,
  label,
}: SheetGridProps) {
  const gridId = useId();
  const rowNav = useRowNav();
  const tableRef = useRef<HTMLTableElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  const slice = useWindowedSlice({ count: rows.length, scroller: 'window', estimatedRowHeight: 46 });

  // The cursor starts on the status column: it is the cell a recruiter changes most.
  const [cursor, setCursor] = useState<CursorState>(() => ({
    row: 0,
    col: Math.max(0, columns.findIndex((c) => c.key === 'status')),
    editing: false,
  }));
  const [active, setActive] = useState(false);

  const rowCount = rows.length;
  const colCount = columns.length;
  const cur: CursorState = {
    row: clamp(cursor.row, rowCount),
    col: clamp(cursor.col, colCount),
    editing: cursor.editing && rowCount > 0 && colCount > 0,
  };
  const curRef = useRef(cur);
  curRef.current = cur;

  // Pinned columns: their widths and left offsets.
  const { leftOf, pinnedWidth, totalWidth } = useMemo(() => {
    const map = new Map<string, number>();
    let left = 0;
    let total = 0;
    for (const col of columns) {
      const w = renderedWidth(col);
      if (PINNED.has(col.key)) {
        map.set(col.key, left);
        left += w;
      }
      total += w;
    }
    return { leftOf: map as ReadonlyMap<string, number>, pinnedWidth: left, totalWidth: total };
  }, [columns]);

  // --- The editing cell, reported to the page -------------------------------------------------
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  const keyEditRef = useRef<EditingCell | null>(null);
  const noteEditRef = useRef<EditingCell | null>(null);
  const report = useCallback(() => {
    onEditingChangeRef.current?.(keyEditRef.current ?? noteEditRef.current);
  }, []);

  const cursorId = rows[cur.row]?.contact.id ?? null;
  const cursorKey = columns[cur.col]?.key ?? null;
  const editId = cur.editing ? cursorId : null;
  const editKey = cur.editing ? cursorKey : null;
  useEffect(() => {
    keyEditRef.current = editId && editKey ? { contactId: editId, column: editKey } : null;
    report();
  }, [editId, editKey, report]);

  // A note editor opened with the mouse counts too.
  const onNoteEditing = useCallback((contactId: string, open: boolean) => {
    if (open) noteEditRef.current = { contactId, column: 'note' };
    else if (noteEditRef.current?.contactId === contactId) noteEditRef.current = null;
    report();
  }, [report]);

  // An editor belongs to a lead, not to a line number: if a different lead lands under the
  // cursor while it is open (a re-sort, a refresh), the editor closes.
  const editLeadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!cursor.editing) {
      editLeadRef.current = null;
      return;
    }
    if (editLeadRef.current === null) {
      editLeadRef.current = cursorId;
      return;
    }
    if (editLeadRef.current !== cursorId) setCursor((c) => ({ ...c, editing: false }));
  }, [cursor.editing, cursorId]);

  // An edit whose control went away. A cell's own dialog (the status cell's reason modal or
  // randomized-conflict acknowledgement) can close with nothing changed, and a save can disable
  // the control under the keyboard: the control that had focus is then gone or unusable, focus
  // falls to <body> (most browsers fire no event for that), and nothing else would end the edit,
  // so the arrows, Esc and Enter would never reach the grid again. While a cell is being edited
  // the grid watches its own DOM; once no dialog is up and focus is nowhere, the edit is over and
  // the grid takes the keyboard back (decision 9).
  const editingNow = cur.editing;
  useEffect(() => {
    const table = tableRef.current;
    if (!editingNow || !table || typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      if (!curRef.current.editing || table.querySelector('[role="dialog"]') || !focusIsNowhere()) return;
      setCursor((c) => (c.editing ? { ...c, editing: false } : c));
      table.focus({ preventScroll: true });
    });
    observer.observe(table, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    return () => observer.disconnect();
  }, [editingNow]);

  // --- The header row under the app bar (see the header comment) ----------------------------
  useEffect(() => {
    const table = tableRef.current;
    const head = headRef.current;
    if (!table || !head) return undefined;
    let frame: number | null = null;
    const update = () => {
      frame = null;
      const rect = table.getBoundingClientRect();
      const headHeight = head.getBoundingClientRect().height;
      const room = Math.max(0, rect.height - headHeight);
      const offset = Math.min(room, Math.max(0, APP_BAR_PX - rect.top));
      const next = `${Math.round(offset)}px`;
      if (table.style.getPropertyValue('--sheet-head-y') !== next) table.style.setProperty('--sheet-head-y', next);
    };
    const schedule = () => {
      if (frame === null) frame = nextFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== null) cancelFrame(frame);
    };
  }, [rowCount]);

  // --- The sideways scrollbar, pinned to the bottom of the window ------------------------------
  // The page scrolls the rows, so the box's own scrollbar sits under the last row, out of reach
  // on a long list. A second bar, sticky at the window's bottom edge, mirrors the box's
  // scrollLeft both ways; the box's own bar is hidden so there is only ever one.
  const barRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [overflowX, setOverflowX] = useState(false);
  useEffect(() => {
    const wrap = wrapRef.current;
    const table = tableRef.current;
    if (!wrap || !table) return undefined;
    const measure = () => {
      setScrollWidth(wrap.scrollWidth);
      setOverflowX(wrap.scrollWidth > wrap.clientWidth + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    observer.observe(table);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const bar = barRef.current;
    if (!wrap || !bar || !overflowX) return undefined;
    const follow = (from: HTMLElement, to: HTMLElement) => () => {
      if (Math.abs(to.scrollLeft - from.scrollLeft) > 0.5) to.scrollLeft = from.scrollLeft;
    };
    const fromWrap = follow(wrap, bar);
    const fromBar = follow(bar, wrap);
    fromWrap();
    wrap.addEventListener('scroll', fromWrap, { passive: true });
    bar.addEventListener('scroll', fromBar, { passive: true });
    return () => {
      wrap.removeEventListener('scroll', fromWrap);
      bar.removeEventListener('scroll', fromBar);
    };
  }, [overflowX]);

  // --- Keyboard ----------------------------------------------------------------------------------
  const focusGrid = useCallback(() => {
    tableRef.current?.focus({ preventScroll: true });
  }, []);

  /** Scroll so the cursor cell is in view: under the header, and clear of the pinned columns. */
  const ensureVisible = useCallback((rowIndex: number, colIndex: number) => {
    nextFrame(() => {
      const table = tableRef.current;
      const wrap = wrapRef.current;
      if (!table || !wrap) return;
      const target = table.querySelector<HTMLElement>(`[data-cell="${rowIndex}:${colIndex}"]`);
      if (!target) {
        // Windowed away: jump near it, and the window renders it.
        const sample = table.querySelector<HTMLElement>('tbody tr[data-row]');
        const pitch = sample ? sample.getBoundingClientRect().height : 0;
        const body = table.tBodies[0];
        if (pitch <= 0 || !body) return;
        const top = body.getBoundingClientRect().top + window.scrollY + rowIndex * pitch;
        window.scrollTo(0, Math.max(0, top - window.innerHeight / 2));
        return;
      }
      const r = target.getBoundingClientRect();
      if (r.height <= 0) return; // not laid out (hidden, or a test document)
      const headHeight = headRef.current ? headRef.current.getBoundingClientRect().height : 0;
      const topLimit = APP_BAR_PX + headHeight;
      // Above the pinned sideways scrollbar (and the mobile tab bar under it) when it shows.
      const bar = barRef.current;
      const bottomLimit = bar && bar.offsetHeight > 0
        ? Math.min(window.innerHeight, bar.getBoundingClientRect().top)
        : window.innerHeight;
      if (r.top < topLimit) window.scrollBy(0, r.top - topLimit - 4);
      else if (r.bottom > bottomLimit - 4) window.scrollBy(0, r.bottom - bottomLimit + 8);
      const key = columns[colIndex]?.key;
      if (!key || PINNED.has(key)) return;
      const w = wrap.getBoundingClientRect();
      if (r.left < w.left + pinnedWidth) wrap.scrollLeft -= w.left + pinnedWidth - r.left;
      else if (r.right > w.right) wrap.scrollLeft += r.right - w.right;
    });
  }, [columns, pinnedWidth]);

  /**
   * An editor finished (a change landed, a note saved, Esc in a note, a menu closed). The grid
   * takes focus back only when focus was in that cell, or nowhere: a click that moved it to
   * another control keeps it there.
   */
  const endEdit = useCallback(() => {
    setCursor((c) => (c.editing ? { ...c, editing: false } : c));
    const table = tableRef.current;
    if (!table) return;
    const cursorCell = table.querySelector('[data-cursor="true"]');
    if (focusIsNowhere() || (cursorCell && cursorCell.contains(document.activeElement))) {
      table.focus({ preventScroll: true });
    }
  }, []);

  const onCellMouseDown = useCallback((rowIndex: number, colIndex: number) => {
    setCursor((c) => (c.row === rowIndex && c.col === colIndex ? c : { row: rowIndex, col: colIndex, editing: false }));
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTableElement>) => {
    const target = e.target as HTMLElement;
    const table = tableRef.current;
    // A dialog raised from a cell (the reason modal, the randomized acknowledgement) owns the keys,
    // including while focus sits behind it on the grid (Enter must not open the cell under it).
    if (target.closest('[role="dialog"]') || table?.querySelector('[role="dialog"]')) return;
    const onGrid = target === table;
    const inEditor = !onGrid && isEditorTarget(target);
    const state = curRef.current;
    const editing = state.editing || inEditor;
    const action = sheetKeyAction(e, editing);
    if (!action) return;
    switch (action.type) {
      case 'move': {
        const next = moveCursor({ ...state, editing }, action.key, rowCount, colCount);
        const tab = action.key === 'Tab' || action.key === 'ShiftTab';
        // At the first or last cell Tab leaves the grid, as it leaves any control.
        if (tab && !editing && next.row === state.row && next.col === state.col) return;
        e.preventDefault();
        setCursor(next);
        focusGrid();
        ensureVisible(next.row, next.col);
        return;
      }
      case 'enter': {
        // A focused button or box answers its own Enter.
        if (!onGrid) return;
        const next = enterCell(state, columns, rowCount);
        if (next !== state) {
          e.preventDefault();
          setCursor(next);
        }
        return;
      }
      case 'escape': {
        if (!editing) return;
        e.preventDefault();
        setCursor(escapeCursor({ ...state, editing: true }));
        focusGrid();
        return;
      }
      case 'open': {
        const lead = rows[state.row];
        if (!lead) return;
        e.preventDefault();
        window.open(`/crm/contacts/${lead.contact.id}`, '_blank', 'noopener');
        return;
      }
      case 'search':
        e.preventDefault();
        onFocusSearch?.();
        return;
      default:
        return;
    }
  };

  // The grid itself taking focus means no editor has it (an editor always holds focus inside its
  // cell), so any editing turn is over and the arrows are the grid's again: after Esc, after an
  // edit landed, or when the recruiter tabs back in after clicking away mid-edit.
  const onGridFocus = (e: ReactFocusEvent<HTMLTableElement>) => {
    setActive(true);
    if (e.target === e.currentTarget) setCursor((c) => (c.editing ? { ...c, editing: false } : c));
  };
  const onGridBlur = (e: ReactFocusEvent<HTMLTableElement>) => {
    const table = e.currentTarget;
    if (table.contains(e.relatedTarget as Node | null)) return;
    setActive(false);
    // Focus leaving the grid for another control, or for the page itself (a click outside), ends
    // the editing turn: the recruiter moved on. Judged once any removal in progress has finished:
    // a control removed or disabled under the keyboard did not move on (the watcher above takes
    // the keyboard back for those), and a switch to another window leaves focus where it was.
    const lost = e.target as HTMLElement;
    void Promise.resolve().then(() => {
      if (!lost.isConnected || isDisabledControl(lost) || table.contains(document.activeElement)) return;
      setCursor((c) => (c.editing ? { ...c, editing: false } : c));
    });
  };

  // --- Header --------------------------------------------------------------------------------
  const sortKey: SheetColumnKey = sort ? sort.key : 'reason';
  const sortDir = sort ? sort.dir : 'asc';
  const cycleSort = (key: SheetColumnKey) => {
    if (sortKey !== key) onSortChange({ key, dir: 'asc' });
    else if (sortDir === 'asc') onSortChange({ key, dir: 'desc' });
    else onSortChange(null);
  };

  const allSelected = rowCount > 0 && rows.every((r) => selection.has(r.contact.id));
  const someSelected = !allSelected && rows.some((r) => selection.has(r.contact.id));

  const headStyle = (col: SheetColumn): CSSProperties => ({
    transform: 'translateY(var(--sheet-head-y, 0px))',
    ...(PINNED.has(col.key) ? { left: leftOf.get(col.key) ?? 0 } : {}),
  });

  const activeId = cur.row >= slice.start && cur.row < slice.end && rowCount > 0
    ? `${gridId}-c-${cur.row}-${cur.col}`
    : undefined;

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div
        ref={wrapRef}
        className="overflow-x-auto rounded-xl [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <table
          ref={tableRef}
          role="grid"
          aria-label={label}
          aria-rowcount={rowCount + 1}
          aria-colcount={colCount}
          aria-multiselectable="true"
          aria-activedescendant={activeId}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onFocus={onGridFocus}
          onBlur={onGridBlur}
          style={{ width: totalWidth, minWidth: '100%' }}
          className="table-fixed border-separate border-spacing-0 text-left text-sm focus:outline-none"
        >
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} style={{ width: renderedWidth(col) }} />
            ))}
          </colgroup>
          <thead ref={headRef}>
            <tr aria-rowindex={1}>
              {columns.map((col) => {
                const pinned = PINNED.has(col.key);
                const sorted = col.sortable && sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={sorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : col.sortable ? 'none' : undefined}
                    style={headStyle(col)}
                    className={`border-b border-gray-200 bg-gray-50 px-2 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 ${
                      pinned ? `sticky ${Z.menu}` : `relative ${Z.rail}`
                    }`}
                  >
                    {col.key === 'select' ? (
                      <span className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={(e) => onSelectAll(e.target.checked)}
                          aria-label="Select every lead on the list"
                          className={CHECKBOX_CLS}
                        />
                      </span>
                    ) : col.sortable ? (
                      <button
                        type="button"
                        onClick={() => cycleSort(col.key)}
                        title={`Sort by ${col.label.toLowerCase()}`}
                        className="inline-flex max-w-full items-center gap-1 rounded uppercase tracking-wider transition-colors duration-150 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:text-gray-200"
                      >
                        <span className="truncate">{col.label}</span>
                        {sorted && (sortDir === 'asc'
                          ? <ArrowUp className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
                          : <ArrowDown className="h-3 w-3 flex-shrink-0" aria-hidden="true" />)}
                      </button>
                    ) : (
                      <span className="truncate">{col.label}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={slice.containerRef}>
            {slice.topPad > 0 && (
              <tr aria-hidden="true" data-spacer="1">
                <td colSpan={colCount} style={{ height: slice.topPad, padding: 0, border: 0 }} />
              </tr>
            )}
            {rows.slice(slice.start, slice.end).map((row, i) => {
              const index = slice.start + i;
              const onCursorRow = index === cur.row;
              return (
                <SheetRow
                  key={row.contact.id}
                  row={row}
                  index={index}
                  columns={columns}
                  study={study}
                  gridId={gridId}
                  leftOf={leftOf}
                  selected={selection.has(row.contact.id)}
                  cursorCol={onCursorRow ? cur.col : -1}
                  editing={onCursorRow && cur.editing}
                  active={active}
                  rowNav={rowNav}
                  onSelect={onSelect}
                  onRowChange={onRowChange}
                  onCellMouseDown={onCellMouseDown}
                  onEditEnd={endEdit}
                  onNoteEditing={onNoteEditing}
                />
              );
            })}
            {slice.bottomPad > 0 && (
              <tr aria-hidden="true" data-spacer="1">
                <td colSpan={colCount} style={{ height: slice.bottomPad, padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div
        ref={barRef}
        aria-hidden="true"
        data-testid="sheet-x-scrollbar"
        className={`sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 ${Z.menu} overflow-x-auto overflow-y-hidden rounded-b-xl border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${
          overflowX ? '' : 'hidden'
        }`}
      >
        <div style={{ width: scrollWidth, height: 1 }} />
      </div>
    </div>
  );
}

export default SheetGrid;
